import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, LoginRequest, RegisterRequest, RegisterResponse, UserUpdateData } from '@kifuerpflege/shared';
import * as authApi from '../features/auth/api/authApi';

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (data: LoginRequest, turnstileToken?: string) => Promise<void>;
  register: (data: RegisterRequest, turnstileToken?: string) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  updateUser: (data: UserUpdateData) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login
      login: async (data: LoginRequest, turnstileToken?: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.login(data, turnstileToken);

          // Store tokens
          localStorage.setItem('accessToken', response.accessToken);
          // Note: Backend should send refreshToken in response or httpOnly cookie
          // For now, we'll generate a placeholder or expect it in the response

          set({
            user: response.user,
            accessToken: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          const errorMessage = error.response?.data?.error?.message || 'Login failed';
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // Register
      register: async (data: RegisterRequest, turnstileToken?: string): Promise<RegisterResponse> => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.register(data, turnstileToken);

          // Registration now requires email verification
          // Don't set user/tokens yet - user needs to verify email first
          set({
            isLoading: false,
          });

          return response;
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.error?.message || 'Registration failed';
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      // Logout
      logout: async () => {
        try {
          const { refreshToken } = get();

          if (refreshToken) {
            await authApi.logout(refreshToken);
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear tokens and state
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null,
          });
        }
      },

      // Load current user
      loadUser: async () => {
        try {
          const accessToken = localStorage.getItem('accessToken');

          if (!accessToken) {
            set({ isAuthenticated: false, user: null });
            return;
          }

          set({ isLoading: true });

          const user = await authApi.getCurrentUser();

          set({
            user,
            accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error('Load user error:', error);
          // Clear invalid tokens
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      // Update user
      updateUser: async (data: UserUpdateData) => {
        try {
          set({ isLoading: true, error: null });

          const updatedUser = await authApi.updateCurrentUser(data);

          set({
            user: updatedUser,
            isLoading: false,
          });
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.error?.message || 'Update failed';
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist necessary fields
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
